import { useMapEvents } from "react-leaflet";
import "../../../../js/Leaflet-WMS-1.1.0/dist/Leaflet-WMS";
import { LS } from "constants/localization";
import MAP from "constants/mapConstants";
import * as L from "leaflet";
import $ from "jquery";

/**
 * This component implements all functionality which is called when the map is clicked.
 * First, the event callback fuction will look for geometries which may have been
 * clicked and displays a popup with their metadata. If no geometry is clicked,
 * a GetFeatureInfo request is called on all visible WMS overlays and results are
 * displayed in a popup. The WMS overlay config should include the infoFormat option.
 */
export default function MapClickEventComponent({
  searchView,
  getFeatureInfoEnabled,
  overlayMapDetails,
}) {
  const isMatchingOverlayDetails = (layer) => {
    if (!layer.wmsParams) {
      return false;
    }
    const params = layer.wmsParams;
    return overlayMapDetails.some(
      (o) => o.id === params.layers && o.provider === params.providerId
    );
  };

  const isGeometry = (layer) => {
    return (
      layer instanceof L.CircleMarker ||
      layer instanceof L.Rectangle ||
      layer instanceof L.Polygon ||
      layer instanceof L.Polyline
    );
  };

  const getPopupTextForFeatureCollection = (featureCollection) => {
    let text = "";
    featureCollection.features.forEach((feature) => {
      text = text + getPopupTextForFeature(feature);
    });
    return text;
  };

  const getPopupTextForFeature = (feature) => {
    let text = [];
    let properties = feature.properties;
    for (const [key, value] of Object.entries(properties)) {
      if (value) {
        const s = value.toString().trim();
        if (s && s !== "null") {
          text.push("<b>" + key + "</b>: " + value);
        }
      }
    }
    return text.join("<br />");
  };

  // returns all geometries saved as search results
  const getGeometries = (map) => {
    const geometries = [];
    map.eachLayer((l) => {
      if (isGeometry(l)) {
        geometries.push(l);
      }
    });
    return geometries;
  };

  // iterates through all geometries saved as search results
  // returns those which intersect the given point
  const getGeometriesAtClickedPoint = (map, latlng) => {
    return getGeometries(map).filter((g) =>
      checkGeometryAtClickedPoint(g, latlng)
    );
  };

  const checkGeometryAtClickedPoint = (layer, latlng) => {
    if (layer instanceof L.CircleMarker) {
      return checkCircleMarkerAtClickedPoint(layer, latlng);
    }
    return checkPolygonAtClickedPoint(layer, latlng);
  };

  // returns true if given polygon intersects given point
  const checkPolygonAtClickedPoint = (layer, latlng) => {
    const x = latlng.lat,
      y = latlng.lng;
    var layerPoints = layer.getLatLngs()[0];
    var inside = false;
    for (
      var i = 0, j = layerPoints.length - 1;
      i < layerPoints.length;
      j = i++
    ) {
      var xi = layerPoints[i].lat,
        yi = layerPoints[i].lng;
      var xj = layerPoints[j].lat,
        yj = layerPoints[j].lng;
      var intersect =
        yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // returns true if given circle marker intersects given point
  const checkCircleMarkerAtClickedPoint = (layer, latlng) => {
    const distance = latlng.distanceTo(layer.getLatLng());
    return distance === 0;
  };

  // display a popup without tabs which shows metadata of one object
  const displaySimplePopup = (map, uri, point) => {
    getImportantInfoPopupContent(uri).then((text) => {
      map.openPopup(text, point, { maxWidth: 600 });
      setLayerSelected(uri);
    });
  };

  async function getImportantInfoPopupContent(uri) {
    const response = await fetch("/mapSearch.do?uri=" + uri);
    const data = await response.json();
    return createImportantInfoPopup(data.fields, uri);
  }

  // Generates search result popup content from important info request response
  function createImportantInfoPopup(fields, uri) {
    let text = "";
    fields.forEach((f) => {
      text =
        text +
        '<div id="' +
        uri +
        '" class="field-container" predicate=' +
        f.predicate +
        ' aria-label="Field">';
      text = text + '<div class="field list">';

      if (f.label !== "Tyyppi" && f.label !== "Type") {
        text = text + '<div class="header flex-left list">';
        text =
          text +
          '<label class="field-header list form-label">' +
          f.label +
          "</label></div>";
      }
      text = text + "<ul>";
      f.values.forEach((v) => {
        text = text + '<li><span class="literal-value">' + v + "</span></li>";
      });
      text = text + "</ul></div></div>";
    });
    text =
      text + '<div class="result-pane__links"><div class="dam-lisaa-lataa">';
    text =
      text +
      '<a class="action-link" target="mainframe" tabindex="0" href="displayObject.do?uri=' +
      uri +
      '&page=1">';
    text =
      text + `<i class="fa fa-sign-in"></i>${LS.actions.open}</a></div></div>`;
    return text;
  }

  // iterates through all geometries saved in search results layer group
  // sets the layer with the specified URI as red, the rest as blue
  function setLayerSelected(uri) {
    getGeometries(map).forEach((layer) => {
      if (layer instanceof L.FeatureGroup) {
        layer.eachLayer((marker) => {
          setLayerColor(marker, uri);
        });
      }
      setLayerColor(layer, uri);
    });
  }

  //set color of selected layer
  function setLayerColor(layer, uri) {
    if (layer.options.uri === uri) {
      layer.setStyle({ color: MAP.SELECTED_COLOR });
    } else {
      layer.setStyle({ color: MAP.DEFAULT_COLOR });
    }
  }

  // sets all layers in the search result layer group as blue
  function setAllLayersUnselected(map) {
    getGeometries(map).forEach((layer) => {
      if (layer instanceof L.MarkerClusterGroup) {
        layer.eachLayer((marker) =>
          marker.setStyle({ color: MAP.DEFAULT_COLOR })
        );
      } else {
        layer.setStyle({ color: MAP.DEFAULT_COLOR });
      }
    });
  }

  const map = useMapEvents({
    popupclose: () => {
      setAllLayersUnselected(map);
    },
    click: (e) => {
      if (
        !map.pm.globalDrawModeEnabled() &&
        !map.pm.globalRemovalModeEnabled()
      ) {
        const point = e.latlng;
        const intersectingGeometries = getGeometriesAtClickedPoint(map, point);
        console.log("clicked geometries", intersectingGeometries);

        // geometry popups handled only in search view
        if (intersectingGeometries.length === 1 && searchView) {
          // one geometry clicked, simple handling
          let uri = intersectingGeometries[0].options.uri;
          displaySimplePopup(map, uri, point);
        } else if (intersectingGeometries.length > 1 && searchView) {
          // more than one geometry clicked, create multitab popup
          let uriArray = [];
          intersectingGeometries.forEach((layer) =>
            uriArray.push(layer.options.uri)
          );
          const uriSet = new Set(uriArray);

          // if multiple geometries represent one object, display simple popup
          if (uriSet.size === 1) {
            displaySimplePopup(uriArray[0], point);
          } else {
            let promises = [];
            uriSet.forEach((uri) => {
              promises.push(getImportantInfoPopupContent(uri));
            });
            let markerTexts = [];
            // wait for all objects important info to be fetched before starting to build popup
            Promise.allSettled(promises).then((result) => {
              let resultCount = 1;
              result.forEach((r) => {
                // wrap each object's important info content in a div that will work as a displayable tab
                const displayStyle = resultCount === 1 ? "block" : "none";
                markerTexts.push(
                  '<div id="layer-tab' +
                    resultCount +
                    '" class="layer-tab" style="display: ' +
                    displayStyle +
                    '">' +
                    r.value +
                    "</div>"
                );

                resultCount++;
              });

              // create tab selection element
              let text = '<ul class="tabs-menu layer-tabs-menu">';
              for (let i = 1; i < resultCount; i++) {
                const liClass = i === 1 ? ' class="current"' : "";
                text += "<li" + liClass + ">";
                text += `<a href="layer-tab${i}">${LS.target} ${i}</a>`;
                text += "</li>";
              }
              text += "</ul>";

              markerTexts.forEach((t) => {
                text += t;
              });

              map.openPopup(text, e.latlng, { maxWidth: 600 });
              let layerObjectURI = $("#layer-tab1 > div").attr("id");
              setLayerSelected(layerObjectURI);

              // tab selection links event
              $(".layer-tabs-menu a").click(function (event) {
                event.preventDefault();
                $(this).parent().addClass("current");
                $(this).parent().siblings().removeClass("current");
                var clickedTabID = $(this).attr("href");

                let tabDivs = $(".layer-tab");
                tabDivs.each(function () {
                  this.style.display =
                    this.id === clickedTabID ? "block" : "none";
                });

                layerObjectURI = $("#" + clickedTabID + " > div").attr("id");
                setLayerSelected(layerObjectURI);
              });
            });
          }
        } else if (getFeatureInfoEnabled) {
          let popupText = "";
          let visibleOverlays = [];
          let layerCount = 0;
          map.eachLayer((l) => {
            if (isMatchingOverlayDetails(l)) {
              visibleOverlays.push(l);
            }
          });
          visibleOverlays.forEach((o) => {
            o.getFeatureInfo({
              latlng: e.latlng,
              providerId: o.options.providerId,
              infoFormat: o.options.infoFormat,
              map: map,
              done: function (featureCollection) {
                popupText =
                  popupText +
                  getPopupTextForFeatureCollection(featureCollection);
                layerCount++;
                if (layerCount === visibleOverlays.length && popupText !== "") {
                  map.openPopup(popupText, e.latlng);
                }
              },
            });
          });
        }
      }
    },
  });

  return null;
}
